// The organisation's add-ons, read-only (F-BOOT-17, spec/25 §17): what the Provider has attached — Clavian Boot for N PCs —
// and what it comes to each month, `PCs × price per PC`, in integer minor units. The Provider attaches and changes them, so
// nothing here edits. Gated on `org:billing`; the server checks it again.

import type { OrganizationAddon } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, EmptyState, Table, type TableColumn } from '@clavian/ui-web';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { useApi } from '../../api/client';
import { useAccess } from '../../api/queries';
import { LoadFailure } from '../../shell/LoadFailure';
import { Icon } from '../../shell/nav-icons';
import { useScopeIds } from '../../shell/scope';
import { Forbidden, Page, Section, SummaryStrip, TableSkeleton } from '../../ui';

import '../../boot/boot.css';

const ORG_BILLING = 'org:billing';

export const organizationAddonKeys = {
  list: (organizationId: string) => ['organization', organizationId, 'addons'] as const,
};

/** The organisation's add-ons and the monthly total (`GET /v1/organizations/{o}/addons`, `org:billing`). */
export function useOrganizationAddonsReadOnly(organizationId: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: organizationAddonKeys.list(organizationId),
    queryFn: () => api.listOrganizationAddons({ path: { organizationId } }),
    enabled,
  });
}

/** `/o/{orgSlug}/add-ons`. */
export function AddonsPage(): ReactNode {
  const { t } = useTranslation('console');
  const scope = useScopeIds();
  const access = useAccess(scope.access);
  const allowed = access.status === 'ready' && access.can(ORG_BILLING);
  const addons = useOrganizationAddonsReadOnly(scope.organizationId, allowed);

  let body: ReactNode;
  if (access.status === 'loading') body = <TableSkeleton rows={3} label={t('addons.loading')} />;
  else if (!allowed) body = <Forbidden message={t('addons.notPermitted')} />;
  else if (addons.data === undefined) {
    body = addons.isError ? <LoadFailure error={addons.error} message={t('addons.unavailable')} onRetry={() => void addons.refetch()} /> : <TableSkeleton rows={3} label={t('addons.loading')} />;
  } else {
    body = <AddonsBody data={addons.data.data} total={addons.data.monthlyTotal} />;
  }
  return (
    <Page title={t('addons.title')} description={t('addons.intro')}>
      {body}
    </Page>
  );
}

function AddonsBody({ data, total }: { readonly data: readonly OrganizationAddon[]; readonly total: OrganizationAddon['monthlyCharge'] }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const active = data.filter((addon) => addon.status === 'active');
  const ended = data.filter((addon) => addon.status !== 'active');

  if (data.length === 0) {
    return <EmptyState icon={<Icon name="addons" />} title={t('addons.empty.title')} description={t('addons.empty.body')} />;
  }

  const columns: readonly TableColumn<OrganizationAddon>[] = [
    { key: 'name', header: t('addons.columns.name'), cell: (addon) => addon.name },
    { key: 'pcs', header: t('addons.columns.pcs'), numeric: true, cell: (addon) => format.number(addon.quantity) },
    { key: 'unit', header: t('addons.columns.unitPrice'), numeric: true, cell: (addon) => t('addons.perPcMonth', { price: format.money(addon.unitPrice) }) },
    // `PCs × price per PC`, as the server worked it out in integer minor units.
    { key: 'month', header: t('addons.columns.monthly'), numeric: true, cell: (addon) => format.money(addon.monthlyCharge) },
    { key: 'since', header: t('addons.columns.since'), cell: (addon) => format.date(addon.startedAt, 'medium') },
  ];

  return (
    <>
      <SummaryStrip label={t('addons.summary')}>
        <p className="console-addons__total">
          <span className="console-muted">{t('addons.totalLabel')}</span> <strong className="clav-figure">{format.money(total)}</strong>
        </p>
      </SummaryStrip>
      <Section title={t('addons.active')} body="rows">
        {active.length === 0 ? (
          <p className="console-muted">{t('addons.noneActive')}</p>
        ) : (
          <Table caption={t('addons.active')} captionHidden columns={columns} rows={active} rowKey={(addon) => addon.id} />
        )}
      </Section>
      {ended.length === 0 ? null : (
        <Section title={t('addons.ended')} body="rows">
          <Table
            caption={t('addons.ended')}
            captionHidden
            columns={[
              ...columns.slice(0, 2),
              { key: 'status', header: t('addons.columns.status'), cell: () => <Badge tone="neutral">{t('addons.cancelled')}</Badge> },
              { key: 'until', header: t('addons.columns.until'), cell: (addon: OrganizationAddon) => (addon.endedAt === null ? '' : format.date(addon.endedAt, 'medium')) },
            ]}
            rows={ended}
            rowKey={(addon) => addon.id}
          />
        </Section>
      )}
      <p className="console-muted">{t('addons.footer')}</p>
    </>
  );
}
